import { StatusBadge } from '@chiroup/components/StatusBadge';
import { SeverityEnum } from '@chiroup/components/enums/severity';
import { ColumnDef, DataTable, DataTableColumnHeader } from '@chiroup/components/ui/data-table';
import {
  BILLING_READINESS_WINDOWS,
  BillingReadinessRow,
  BillingReadinessSeverity,
  BillingReadinessWindow,
  DEFAULT_BILLING_READINESS_WINDOW,
} from '@chiroup/core/types/BillingReadiness.type';
import React, { useState } from 'react';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import useBillingReadiness from '../../hooks/useBillingReadiness';
import LoadingPage from '../common/LoadingPage';
import Header from '../layout/Header';
import BillingReadinessDetailModal from './BillingReadinessDetailModal';

const SEVERITY_META: Record<BillingReadinessSeverity, { severity: SeverityEnum; label: string }> = {
  [BillingReadinessSeverity.Critical]: { severity: SeverityEnum.Error, label: 'Critical' },
  [BillingReadinessSeverity.Moderate]: { severity: SeverityEnum.Warning, label: 'Moderate' },
  [BillingReadinessSeverity.Normal]: { severity: SeverityEnum.Success, label: 'Normal' },
  [BillingReadinessSeverity.None]: { severity: SeverityEnum.Note, label: '—' },
};

/** Numeric rank so columns sort by urgency (Critical first) regardless of label. */
const severityRank: Record<BillingReadinessSeverity, number> = {
  [BillingReadinessSeverity.Critical]: 3,
  [BillingReadinessSeverity.Moderate]: 2,
  [BillingReadinessSeverity.Normal]: 1,
  [BillingReadinessSeverity.None]: 0,
};

const SignalBadge: React.FC<{ severity: BillingReadinessSeverity; text: string }> = ({
  severity,
  text,
}) => {
  const meta = SEVERITY_META[severity];
  return <StatusBadge severity={meta.severity} status={text} />;
};

/** A ratio cell: color-coded percentage pill + the raw count, kept subtle. */
const RatioCell: React.FC<{
  severity: BillingReadinessSeverity;
  pct: number;
  numerator: number;
  denominator: number;
}> = ({ severity, pct, numerator, denominator }) => (
  <div className="flex items-center gap-2">
    <SignalBadge severity={severity} text={`${pct}%`} />
    <span className="text-xs text-gray-400 dark:text-dark-gray-500">
      {numerator}/{denominator}
    </span>
  </div>
);

const WINDOW_LABELS: Record<BillingReadinessWindow, string> = {
  7: 'Last 7 days',
  30: 'Last 30 days',
  60: 'Last 60 days',
  90: 'Last 90 days',
};

const columns: ColumnDef<BillingReadinessRow>[] = [
  {
    id: 'clinic',
    // Combine name + id so the toolbar search matches both.
    accessorFn: row => `${row.clinicName} #${row.clinicId}`,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Clinic" />,
    cell: ({ row }) => (
      <div className="font-medium text-gray-900 dark:text-white">
        {row.original.clinicName}
        <span className="ml-2 text-xs text-gray-400">#{row.original.clinicId}</span>
      </div>
    ),
  },
  {
    id: 'transmission',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Claim transmission" />,
    accessorFn: row => severityRank[row.transmission.severity],
    cell: ({ row }) => {
      const { businessDaysSince, severity } = row.original.transmission;
      const text = businessDaysSince == null ? 'No history' : `${businessDaysSince} business days`;
      return <SignalBadge severity={severity} text={text} />;
    },
  },
  {
    id: 'rejection',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Rejection ratio" />,
    accessorFn: row => severityRank[row.rejection.severity],
    cell: ({ row }) => {
      const { available, numerator, denominator, pct, severity } = row.original.rejection;
      if (!available) return <span className="text-xs text-gray-400">Not available</span>;
      if (pct == null) return <span className="text-xs text-gray-400">No data</span>;
      return (
        <RatioCell severity={severity} pct={pct} numerator={numerator} denominator={denominator} />
      );
    },
  },
  {
    id: 'denial',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Denial ratio" />,
    accessorFn: row => severityRank[row.denial.severity],
    cell: ({ row }) => {
      const { numerator, denominator, pct, severity } = row.original.denial;
      if (pct == null) return <span className="text-xs text-gray-400">No ERA data</span>;
      return (
        <RatioCell severity={severity} pct={pct} numerator={numerator} denominator={denominator} />
      );
    },
  },
  {
    id: 'lastPayment',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last posted payment" />,
    accessorFn: row => row.lastPayment.daysSince ?? -1,
    cell: ({ row }) => {
      const { daysSince, severity } = row.original.lastPayment;
      if (daysSince == null) {
        return <span className="text-xs text-gray-400">No payment history</span>;
      }
      return <SignalBadge severity={severity} text={`${daysSince} days ago`} />;
    },
  },
  {
    id: 'lastEra',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last ERA received" />,
    accessorFn: row => row.lastEra.daysSince ?? -1,
    cell: ({ row }) => {
      const { daysSince, severity } = row.original.lastEra;
      if (daysSince == null) return <span className="text-xs text-gray-400">No ERA history</span>;
      return <SignalBadge severity={severity} text={`${daysSince} days ago`} />;
    },
  },
];

const BillingReadinessReport: React.FC = () => {
  const { hasPermission, isLoading: permissionsLoading } = useAdminPermissions();
  const canView = hasPermission('analytics.view');
  const [window, setWindow] = useState<BillingReadinessWindow>(DEFAULT_BILLING_READINESS_WINDOW);
  const [detail, setDetail] = useState<{ id: number; name: string } | null>(null);
  const { data, isFetching } = useBillingReadiness(window, canView);

  if (permissionsLoading) {
    return <LoadingPage />;
  }

  if (!canView) {
    return (
      <>
        <Header title="Billing Readiness Report" />
        <div className="p-6 text-sm text-gray-500 dark:text-dark-gray-400">
          You do not have permission to view the billing readiness report.
        </div>
      </>
    );
  }

  const windowButtons = (
    <div className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 p-0.5 dark:border-dark-gray-600 dark:bg-dark-gray-800">
      {BILLING_READINESS_WINDOWS.map(value => (
        <button
          key={value}
          onClick={() => setWindow(value)}
          className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
            window === value
              ? 'bg-primary-600 text-white shadow-sm'
              : 'text-gray-600 hover:text-gray-900 dark:text-dark-gray-300 dark:hover:text-white'
          }`}
        >
          {WINDOW_LABELS[value]}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <Header title="Billing Readiness Report" />
      <div className="p-6">
        <DataTable
          columns={columns}
          data={data?.rows ?? []}
          fetching={isFetching}
          pagination={{ pageSize: 25 }}
          onRowClick={row => setDetail({ id: row.clinicId, name: row.clinicName })}
          toolbar={{
            search: { column: 'clinic', placeholder: 'Search by clinic name or ID...' },
            rightSlot: windowButtons,
          }}
        />
      </div>
      {detail && (
        <BillingReadinessDetailModal
          clinicId={detail.id}
          clinicName={detail.name}
          window={window}
          onClose={() => setDetail(null)}
        />
      )}
    </>
  );
};

export default BillingReadinessReport;
